import React, { useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/layout/AdminLayout';
import { FiUsers, FiMessageSquare, FiAlertCircle, FiCreditCard, FiBarChart2, FiPieChart } from 'react-icons/fi';
import { FiArrowUpRight, FiArrowDownRight, FiDollarSign, FiTrendingUp, FiCalendar } from 'react-icons/fi';

const StatCard = ({ icon, label, value, trend, trendValue }: { icon: React.ReactNode, label: string, value: string, trend: 'up' | 'down' | 'neutral', trendValue: string }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-foreground/80 backdrop-blur-md border border-border rounded-xl p-5 shadow-sm hover:shadow-glow-sm transition-all duration-300"
    >
      <div className="flex items-start justify-between">
        <div className="p-3 rounded-xl bg-primary/10 text-primary">
          {icon}
        </div>
        {trend === 'up' ? (
          <span className="flex items-center text-emerald-500 text-sm font-medium">
            <FiArrowUpRight size={14} className="mr-1" />
            {trendValue}
          </span>
        ) : trend === 'down' ? (
          <span className="flex items-center text-rose-500 text-sm font-medium">
            <FiArrowDownRight size={14} className="mr-1" />
            {trendValue}
          </span>
        ) : (
          <span className="flex items-center text-gray-500 text-sm font-medium">
            {trendValue}
          </span>
        )}
      </div>
      <h3 className="text-2xl font-bold mt-4">{value}</h3>
      <p className="text-text/70 text-sm mt-1">{label}</p>
    </motion.div>
  );
};

const ChartCard = ({ title, icon, children }: { title: string, icon: React.ReactNode, children: React.ReactNode }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="bg-foreground/80 backdrop-blur-md border border-border rounded-xl p-5 shadow-sm h-full"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">{title}</h2>
        <div className="p-2 rounded-lg bg-primary/10 text-primary">
          {icon}
        </div>
      </div>
      {children}
    </motion.div>
  );
};

const TableCard = ({ title, children }: { title: string, children: React.ReactNode }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
      className="bg-foreground/80 backdrop-blur-md border border-border rounded-xl overflow-hidden shadow-sm"
    >
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      {children}
    </motion.div>
  );
};

interface StatusBadgeProps {
  status: 'active' | 'inactive' | 'pending' | 'connected';
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const getStatusStyles = () => {
    switch (status) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'pending':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'connected':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'inactive':
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full border ${getStatusStyles()}`}>
      {status}
    </span>
  );
};

const AdminDashboard: React.FC = () => {
  const [period, setPeriod] = useState('month');
  
  // Mock data
  const stats = [
    { label: 'Total Users', value: '12,543', icon: <FiUsers size={20} />, trend: 'up' as const, trendValue: '+12.5%' },
    { label: 'Active Chats', value: '34', icon: <FiMessageSquare size={20} />, trend: 'down' as const, trendValue: '-3.4%' },
    { label: 'Expert Requests', value: '8', icon: <FiAlertCircle size={20} />, trend: 'up' as const, trendValue: '+28.1%' },
    { label: 'Transactions', value: '$51,243', icon: <FiCreditCard size={20} />, trend: 'up' as const, trendValue: '+6.8%' },
  ];
  
  const recentUsers = [
    { id: 1, name: 'Sarah Johnson', email: 'sarah.j@example.com', status: 'active' as const, lastLogin: '5 min ago' },
    { id: 2, name: 'Michael Chen', email: 'michael.c@example.com', status: 'active' as const, lastLogin: '2 hours ago' },
    { id: 3, name: 'Emily Rodriguez', email: 'emily.r@example.com', status: 'inactive' as const, lastLogin: '1 day ago' },
    { id: 4, name: 'David Kim', email: 'david.k@example.com', status: 'active' as const, lastLogin: '3 hours ago' },
    { id: 5, name: 'Jessica Lee', email: 'jessica.l@example.com', status: 'pending' as const, lastLogin: 'Never' },
  ];
  
  const recentExpertRequests = [
    { id: 1, user: 'Sarah Johnson', topic: 'Investment advice', status: 'pending' as const, time: '5 min ago' },
    { id: 2, user: 'Michael Chen', topic: 'Loan application', status: 'connected' as const, time: '1 hour ago' },
    { id: 3, user: 'Emily Rodriguez', topic: 'Retirement planning', status: 'pending' as const, time: '3 hours ago' },
  ];

  const featureHighlight = [
    { title: 'Financial Overview', description: 'View key metrics and financial data at a glance', icon: <FiDollarSign size={20} /> },
    { title: 'Performance Analytics', description: 'Track user engagement and platform growth', icon: <FiTrendingUp size={20} /> },
    { title: 'Upcoming Features', description: 'New AI-powered insights coming next month', icon: <FiCalendar size={20} /> },
  ];
  
  return (
    <AdminLayout>
      <div className="container mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between mb-8"
        >
          <div>
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="text-text/70 mt-1">Welcome back, Admin. Here's what's happening today.</p>
          </div>
          
          <div className="flex items-center space-x-2 mt-4 md:mt-0">
            <div className="bg-foreground/70 backdrop-blur-sm border border-border rounded-lg p-1 flex">
              <button
                className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                  period === 'day' ? 'bg-primary text-white shadow-glow-sm' : 'hover:bg-card/70'
                }`}
                onClick={() => setPeriod('day')}
              >
                Day
              </button>
              <button
                className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                  period === 'week' ? 'bg-primary text-white shadow-glow-sm' : 'hover:bg-card/70'
                }`}
                onClick={() => setPeriod('week')}
              >
                Week
              </button>
              <button
                className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                  period === 'month' ? 'bg-primary text-white shadow-glow-sm' : 'hover:bg-card/70'
                }`}
                onClick={() => setPeriod('month')}
              >
                Month
              </button>
            </div>
          </div>
        </motion.div>
        
        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {stats.map((stat, index) => (
            <StatCard 
              key={index} 
              icon={stat.icon} 
              label={stat.label} 
              value={stat.value} 
              trend={stat.trend} 
              trendValue={stat.trendValue} 
            />
          ))}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Charts section */}
          <ChartCard title="User Growth" icon={<FiBarChart2 size={18} />}>
            <div className="h-64 flex items-center justify-center relative">
              {/* Placeholder for chart */}
              <div className="w-full h-full bg-card/20 rounded-lg flex items-center justify-center overflow-hidden relative">
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-primary/20 to-transparent"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 h-60 rounded-full bg-gradient-to-r from-primary/10 to-purple-500/10 blur-xl"></div>
                </div>
                <p className="text-text/70 relative z-10">User Growth Chart</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between mt-4 text-sm">
              <div>
                <p className="text-text/60">Current</p>
                <p className="font-medium">12,543</p>
              </div>
              <div>
                <p className="text-text/60">Growth Rate</p>
                <p className="font-medium text-emerald-500 flex items-center">
                  <FiArrowUpRight size={14} className="mr-1" />
                  12.5%
                </p>
              </div>
              <div>
                <p className="text-text/60">Target</p>
                <p className="font-medium">15,000</p>
              </div>
            </div>
          </ChartCard>
          
          <ChartCard title="Support Categories" icon={<FiPieChart size={18} />}>
            <div className="h-64 flex items-center justify-center relative">
              {/* Placeholder for chart */}
              <div className="w-full h-full bg-card/20 rounded-lg flex items-center justify-center overflow-hidden relative">
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 h-60 rounded-full bg-gradient-to-r from-primary/10 to-blue-500/10 blur-xl"></div>
                </div>
                <p className="text-text/70 relative z-10">Categories Distribution Chart</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="text-sm p-2 rounded-lg bg-card/30 border border-border/50">
                <div className="flex items-center">
                  <span className="h-2 w-2 rounded-full bg-primary mr-2"></span>
                  <span className="text-xs text-text/80">Investments</span>
                </div>
                <p className="font-medium mt-1">34%</p>
              </div>
              <div className="text-sm p-2 rounded-lg bg-card/30 border border-border/50">
                <div className="flex items-center">
                  <span className="h-2 w-2 rounded-full bg-purple-500 mr-2"></span>
                  <span className="text-xs text-text/80">Loans</span>
                </div>
                <p className="font-medium mt-1">28%</p>
              </div>
              <div className="text-sm p-2 rounded-lg bg-card/30 border border-border/50">
                <div className="flex items-center">
                  <span className="h-2 w-2 rounded-full bg-blue-500 mr-2"></span>
                  <span className="text-xs text-text/80">Other</span>
                </div>
                <p className="font-medium mt-1">38%</p>
              </div>
            </div>
          </ChartCard>
        </div>

        {/* Feature Highlight */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          className="mb-8 p-5 bg-gradient-to-r from-primary/5 to-transparent border border-primary/20 rounded-xl backdrop-blur-sm"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Matrix Finance Features</h2>
            <button className="mt-2 md:mt-0 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary text-sm rounded-lg transition-colors">
              View All Features
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {featureHighlight.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="p-4 bg-card/30 border border-border rounded-lg hover:shadow-sm transition-all"
              >
                <div className="flex items-start">
                  <div className="p-2 mr-3 rounded-lg bg-primary/10 text-primary">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-medium">{feature.title}</h3>
                    <p className="text-sm text-text/70 mt-1">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent users */}
          <TableCard title="Recent Users">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-card/30">
                    <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Last Activity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recentUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-card/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-r from-primary/30 to-primary/10 flex items-center justify-center text-primary font-medium mr-3">
                            {user.name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-medium">{user.name}</div>
                            <div className="text-text/50 text-sm">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={user.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text/70">
                        {user.lastLogin}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="px-6 py-3 border-t border-border">
              <button className="text-primary text-sm font-medium hover:text-primary/80 transition-colors">View All Users</button>
            </div>
          </TableCard>
          
          {/* Recent expert requests */}
          <TableCard title="Expert Requests">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-card/30">
                    <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Topic</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recentExpertRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-card/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-r from-primary/30 to-primary/10 flex items-center justify-center text-primary font-medium mr-3">
                            {request.user.charAt(0)}
                          </div>
                          <div className="font-medium">{request.user}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.topic}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={request.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text/70">
                        {request.time}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="px-6 py-3 border-t border-border">
              <button className="text-primary text-sm font-medium hover:text-primary/80 transition-colors">View All Requests</button>
            </div>
          </TableCard>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard; 